
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface HealthCheck {
  name: string;
  status: 'healthy' | 'warning' | 'error' | 'checking';
  message: string;
  details?: string;
}

const HealthChecker: React.FC = () => {
  const [checks, setChecks] = useState<HealthCheck[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const healthChecks = [
    {
      name: 'Database Connection',
      check: async () => {
        const { data, error } = await supabase.from('psychologists').select('id').limit(1);
        if (error) throw new Error(error.message);
        return 'Conexão com banco de dados funcionando';
      }
    },
    {
      name: 'Authentication',
      check: async () => {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw new Error(error.message);
        if (!user) throw new Error('Usuário não autenticado');
        return `Usuário autenticado: ${user.email}`;
      }
    },
    {
      name: 'Edge Functions',
      check: async () => {
        try {
          const { data, error } = await supabase.functions.invoke('generate-financial-report', {
            body: { test: true }
          });
          // Mesmo com erro, se a função responder, ela existe
          return 'Edge functions disponíveis';
        } catch (error) {
          if (error.message?.includes('Function not found')) {
            throw new Error('Edge functions não deployadas');
          }
          return 'Edge functions disponíveis (com erro esperado no teste)';
        }
      }
    },
    {
      name: 'Storage Access',
      check: async () => {
        const { data, error } = await supabase.storage.from('reports').list('', { limit: 1 });
        if (error && !error.message.includes('does not exist')) {
          throw new Error(error.message);
        }
        return 'Acesso ao storage funcionando';
      }
    }
  ];

  const runHealthChecks = async () => {
    setIsRunning(true);
    const results: HealthCheck[] = [];

    for (const healthCheck of healthChecks) {
      // Inicializar com status checking
      const checkItem: HealthCheck = {
        name: healthCheck.name,
        status: 'checking',
        message: 'Verificando...'
      };
      
      setChecks(prev => [...prev.filter(c => c.name !== healthCheck.name), checkItem]);

      try {
        const message = await healthCheck.check();
        checkItem.status = 'healthy';
        checkItem.message = message;
      } catch (error: any) {
        checkItem.status = 'error';
        checkItem.message = 'Falha na verificação';
        checkItem.details = error.message;
      }

      results.push(checkItem);
      setChecks(prev => [...prev.filter(c => c.name !== healthCheck.name), checkItem]);
    }

    setIsRunning(false);
    
    const errorCount = results.filter(r => r.status === 'error').length;
    if (errorCount === 0) {
      toast.success('Todos os testes passaram!');
    } else {
      toast.error(`${errorCount} teste(s) falharam`);
    }
  };

  useEffect(() => {
    runHealthChecks();
  }, []);

  const getStatusIcon = (status: HealthCheck['status']) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'checking':
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />;
    }
  };

  const getStatusBadge = (status: HealthCheck['status']) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="default" className="bg-green-100 text-green-800">OK</Badge>;
      case 'warning':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Aviso</Badge>;
      case 'error':
        return <Badge variant="destructive">Erro</Badge>;
      case 'checking':
        return <Badge variant="outline">Verificando</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Verificação de Saúde do Sistema
          </CardTitle>
          <Button 
            onClick={runHealthChecks} 
            disabled={isRunning}
            size="sm"
            variant="outline"
          >
            {isRunning ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Verificar Novamente
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {checks.map((check) => (
            <div key={check.name} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-3">
                {getStatusIcon(check.status)}
                <div>
                  <div className="font-medium">{check.name}</div>
                  <div className="text-sm text-muted-foreground">{check.message}</div>
                  {check.details && (
                    <div className="text-xs text-red-600 mt-1">{check.details}</div>
                  )}
                </div>
              </div>
              {getStatusBadge(check.status)}
            </div>
          ))}
        </div>
        
        {checks.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            Clique em "Verificar Novamente" para executar os testes
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HealthChecker;
